/**
 * Диалог параметров импорта зданий из OSM по нарисованному полигону.
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { osmImportsApi } from "@/services/api-v2.service";
import { useToast } from "@/hooks/use-toast";
import type { OsmGrouping, OsmImportCreateResponse } from "@/types/osm-import";
import { messageFromThrownApiError } from "@/lib/geometry-api-errors";

interface OsmImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Внешнее кольцо полигона [lon, lat][] */
  coordinates: number[][];
  onImported: (result: OsmImportCreateResponse) => void;
}

export function OsmImportDialog({
  open,
  onOpenChange,
  coordinates,
  onImported,
}: OsmImportDialogProps) {
  const { toast } = useToast();
  const [grouping, setGrouping] = useState<OsmGrouping>("per_building");
  const [name, setName] = useState("");
  const [objectKlsId, setObjectKlsId] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await osmImportsApi.create({
        coordinates,
        grouping,
        name: grouping === "single_object" ? name || undefined : undefined,
        object_kls_id: objectKlsId.trim() ? Number(objectKlsId) : undefined,
      });
      if (res.template_warning) {
        toast({
          title: "Импорт выполнен",
          description: res.template_warning,
        });
      } else {
        toast({
          title: "Импорт выполнен",
          description: `Объектов: ${res.objects}, геометрий: ${res.geometries}`,
        });
      }
      onImported(res);
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Ошибка импорта OSM",
        description: messageFromThrownApiError(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md z-[220]">
        <DialogHeader>
          <DialogTitle>Создать объект из OSM</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Группировка</Label>
            <RadioGroup
              value={grouping}
              onValueChange={(v) => setGrouping(v as OsmGrouping)}
              className="gap-2"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="per_building" id="g-per" />
                <Label htmlFor="g-per" className="font-normal">
                  Каждое здание — отдельный объект
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="single_object" id="g-single" />
                <Label htmlFor="g-single" className="font-normal">
                  Все здания — один объект
                </Label>
              </div>
            </RadioGroup>
          </div>
          {grouping === "single_object" && (
            <div className="space-y-2">
              <Label htmlFor="osm-name">Название</Label>
              <Input
                id="osm-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Объект из OSM"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="osm-kls">Тип объекта (KLS id, опционально)</Label>
            <Input
              id="osm-kls"
              value={objectKlsId}
              onChange={(e) => setObjectKlsId(e.target.value)}
              placeholder="По умолчанию — Здание (OSM)"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Отмена
          </Button>
          <Button onClick={handleSubmit} disabled={loading || coordinates.length < 4}>
            {loading ? "Импорт…" : "Импортировать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
